import React from 'react';
import { ENTITY } from '../entities';
import { ITransformerFunction } from '../../components/EntityEditFetcher';
import { IField, SubForms, SubFormListFieldType } from '../../types/form';
import { t } from '../labels';
import { generateRandomID, IChoice, toRequired } from '../misc';
import { entity2String } from '../toString';
import {
	AsyncQuery,
	generateAsyncQuery,
	generatePaginatedSelectQuery,
} from '../graphql';
import {
	generateBaseField,
	generateStringListField,
	generateSubFormField,
	subFormsConstructors,
	toAsyncChoices,
} from '../form';
import { ERROR_CODES } from '../validation';
import {
	applyRelationships360Bulk,
	bidirectionalCreatePairs,
	findJoinIds,
	findKeyedJoinIds,
	keyedCreatePair,
	nameSearch,
	parseLinks,
	RelationLinks,
	RELATIONS_LINKS_QUERY,
	titleSearch,
	trendNameSearch,
} from './relationships360';
import { client } from '../client';
import { DataVizSelectQuery } from '../../graphql/query/DataVizSelectQuery';

/**
 * Configurazione del form di creazione Trend360.
 *
 * Un Trend360 ha tre tipologie (macrotrend / trend potenziale / trend confermato) che
 * condividono la stessa tabella e mutation: cambiano solo i campi esposti. Questo modulo
 * fornisce, per tipologia:
 *  - la disposizione dei campi (PLACEMENT_BY_TYPE, unica fonte di verita' da cui derivano
 *    la whitelist backend e i campi del dettaglio);
 *  - il transformer che adatta i campi generati dall'introspezione (obbligatorieta',
 *    sezioni, gruppi ripetibili su colonne JSON, select di relazione multiple);
 *  - i gestori di salvataggio (`trend360SaveHandlers`): le relazioni multiple NON fanno
 *    parte della Trend360CreateInput e vengono scritte dopo la creazione via
 *    relationships360Bulk.
 */

export type Trend360TypeValue =
	| 'MACROTREND'
	| 'POTENTIAL_TREND'
	| 'CONFIRMED_TREND';

const TREND360_TYPES: ReadonlyArray<Trend360TypeValue> = [
	'MACROTREND',
	'POTENTIAL_TREND',
	'CONFIRMED_TREND',
];

export const TREND360_TYPE_CHOICES: ReadonlyArray<IChoice> = TREND360_TYPES.map(
	(value) => ({
		value,
		label: t(['Trend360Type', value, 'label'].join('/')),
	}),
);

// ------------------------------------------------------------------ query dei picker

// DataVizFilterInput non supporta la ricerca testuale: si elencano tutti quelli
// riservati a Trend360 (scope: TREND_360). Un editor deve prima "riservare" un DataViz
// dallo schermo generale di DataViz prima di poterlo collegare qui - cosi' questi tre
// slot non si riempiono di dashboard generiche senza relazione con i trend.
const dataVizAsyncQuery = generateAsyncQuery(DataVizSelectQuery, true, () => ({
	filter: { scope: 'TREND_360' },
}));

// trend360s non supporta orderBy:name -> order=false (come nella Relations360Modal)
const trendAsyncQuery = generateAsyncQuery(
	generatePaginatedSelectQuery(ENTITY.TREND_360, false),
	true,
	trendNameSearch,
);

// il Macrotrend padre puo' essere solo un Trend360 di tipo MACROTREND
const parentMacrotrendAsyncQuery = generateAsyncQuery(
	generatePaginatedSelectQuery(ENTITY.TREND_360, false),
	true,
	(text: string) => ({
		filter: { trendName: text, trendTypes: ['MACROTREND'] },
	}),
);

// ------------------------------------------------------------------ gruppi ripetibili

const REPEATABLES: {
	[name: string]: { form: SubForms; addLabelKey: string };
} = {
	signalSources: {
		form: SubForms.TREND_SIGNAL_SOURCE,
		addLabelKey: 'Trend360/signalSources/add',
	},
	earlySignals: {
		form: SubForms.TREND_EARLY_SIGNAL,
		addLabelKey: 'Trend360/earlySignals/add',
	},
	whatToMonitor: {
		form: SubForms.TREND_TEXT_ITEM,
		addLabelKey: 'Trend360/whatToMonitor/add',
	},
};

// campi ripetibili le cui righe sono un solo testo -> salvati come array di stringhe
const TEXT_LIST_FIELDS = ['whatToMonitor'];
// campi ripetibili con righe strutturate -> salvati come array di oggetti
const OBJECT_LIST_FIELDS = ['signalSources', 'earlySignals'];
// tutte le colonne che contengono un array JSON: nel dettaglio vanno rese leggibili
// (i tag NON stanno in TEXT_LIST_FIELDS: li serializza gia' il campo a chip)
const JSON_LIST_FIELDS = TEXT_LIST_FIELDS.concat(OBJECT_LIST_FIELDS).concat([
	'tags',
]);

const toRepeatableField = (name: string): SubFormListFieldType =>
	({
		...generateSubFormField(
			name,
			t(['Trend360', name, 'label'].join('/')),
			[REPEATABLES[name].form],
			false,
		),
		addButtonLabel: t(REPEATABLES[name].addLabelKey),
	} as SubFormListFieldType);

// ------------------------------------------------------------------ relazioni multiple

interface TrendRelationDef {
	name: string;
	entity: ENTITY;
	search?: (text: string) => any;
	max?: number;
}

const RELATIONS_BY_TYPE: { [key: string]: ReadonlyArray<TrendRelationDef> } = {
	MACROTREND: [],
	POTENTIAL_TREND: [
		{ name: 'rel_examples', entity: ENTITY.GOOD_360 },
		{ name: 'rel_relatedTrends', entity: ENTITY.TREND_360, search: trendNameSearch },
	],
	CONFIRMED_TREND: [
		{
			name: 'rel_mainCategories',
			entity: ENTITY.EDITORIAL_SECTION,
			search: nameSearch,
			max: 3,
		},
		{ name: 'rel_products', entity: ENTITY.GOOD_360 },
		{ name: 'rel_companies', entity: ENTITY.COMPANY_360 },
		{ name: 'rel_magazines', entity: ENTITY.MAGAZINE, search: nameSearch },
		{
			name: 'rel_newsletterIssues',
			entity: ENTITY.NEWSLETTER_ISSUE,
			search: nameSearch,
		},
		{ name: 'rel_advs', entity: ENTITY.ADV, search: titleSearch },
		{ name: 'rel_awards', entity: ENTITY.AWARD, search: nameSearch },
	],
};

const maxChoices = (max: number) => (value: any) =>
	Array.isArray(value) && value.length > max
		? new Error(ERROR_CODES.MAX)
		: false;

const toRelationField = (rel: TrendRelationDef): IField => {
	const field: any = toAsyncChoices(
		rel.entity,
		generateAsyncQuery(
			generatePaginatedSelectQuery(rel.entity, false),
			true,
			rel.search || (() => ({ filter: {} })),
		),
		false,
		generateBaseField(rel.name, t(['Trend360', rel.name, 'label'].join('/'))),
	);
	if (rel.max) {
		field.validators = field.validators.concat([maxChoices(rel.max)]);
		field.helpText = t(['Trend360', rel.name, 'helpText'].join('/'));
	}
	return field;
};

// ------------------------------------------------------------------ ordine e sezioni

// unica fonte di verita' sulla composizione dei form: ordine, sezioni e appartenenza
// dei campi per tipologia. Whitelist backend e campi del dettaglio sono DERIVATI da qui.
interface FieldPlacement {
	name: string;
	section?: string;
}

const SECTION = {
	general: 'Trend360/section/general',
	classification: 'Trend360/section/classification',
	media: 'Trend360/section/media',
	content: 'Trend360/section/content',
	signals: 'Trend360/section/signals',
	analysis: 'Trend360/section/analysis',
	relations: 'Trend360/section/relations',
};

const PLACEMENT_BY_TYPE: { [key: string]: ReadonlyArray<FieldPlacement> } = {
	MACROTREND: [
		{ name: 'trendName', section: SECTION.general },
		{ name: 'firstDetected' },
		{ name: 'nextReview' },
		{ name: 'marketStatus', section: SECTION.classification },
		{ name: 'trendIconMedia', section: SECTION.media },
		{ name: 'heroImageMedia' },
		{ name: 'intro', section: SECTION.content },
		{ name: 'content' },
		{ name: 'relkey_MACROTREND_SCORE_EVOLUTION', section: SECTION.relations },
	],
	POTENTIAL_TREND: [
		{ name: 'trendName', section: SECTION.general },
		{ name: 'relkey_PARENT_MACROTREND' },
		{ name: 'firstDetected' },
		{ name: 'nextReview' },
		{ name: 'origin', section: SECTION.classification },
		{ name: 'monitoringStatus' },
		{ name: 'trendIconMedia', section: SECTION.media },
		{ name: 'heroImageMedia' },
		{ name: 'intro', section: SECTION.content },
		{ name: 'content' },
		{ name: 'tags' },
		{ name: 'signalSources', section: SECTION.signals },
		{ name: 'earlySignals' },
		{ name: 'relkey_MARKET_PULSE', section: SECTION.relations },
		{ name: 'rel_examples' },
		{ name: 'rel_relatedTrends' },
	],
	CONFIRMED_TREND: [
		{ name: 'trendName', section: SECTION.general },
		{ name: 'relkey_PARENT_MACROTREND' },
		{ name: 'firstDetected' },
		{ name: 'nextReview' },
		{ name: 'trendStatus', section: SECTION.classification },
		{ name: 'evolution' },
		{ name: 'origin' },
		{ name: 'trendIconMedia', section: SECTION.media },
		{ name: 'heroImageMedia' },
		{ name: 'intro', section: SECTION.content },
		{ name: 'content' },
		{ name: 'whatToMonitor', section: SECTION.analysis },
		{ name: 'rel_mainCategories', section: SECTION.relations },
		{ name: 'rel_products' },
		{ name: 'rel_companies' },
		{ name: 'rel_magazines' },
		{ name: 'rel_newsletterIssues' },
		{ name: 'rel_advs' },
		{ name: 'rel_awards' },
		{ name: 'relkey_ANTITHESIS_TREND' },
		{ name: 'relkey_MARKET_PULSE' },
		{ name: 'relkey_CONFIRMED_COMMUNITY_SURVEY' },
	],
};

const isRelationFieldName = (name: string) => name.indexOf('rel_') === 0;

/**
 * Slot "keyed": riferimenti singoli ad un altro record 360 che NON sono colonne di
 * Trend360 ma Relationships360 marcate con una relation_key. Nel form sono campi
 * sintetici (prefisso relkey_) che il salvataggio traduce in relazioni.
 */
const KEYED_SLOT_PREFIX = 'relkey_';

interface KeyedSlotDef {
	relationKey: string;
	entity: ENTITY;
	asyncQuery: AsyncQuery;
}

const KEYED_SLOTS: ReadonlyArray<KeyedSlotDef> = [
	{
		relationKey: 'PARENT_MACROTREND',
		entity: ENTITY.TREND_360,
		asyncQuery: parentMacrotrendAsyncQuery,
	},
	{
		relationKey: 'ANTITHESIS_TREND',
		entity: ENTITY.TREND_360,
		asyncQuery: trendAsyncQuery,
	},
	{
		relationKey: 'MACROTREND_SCORE_EVOLUTION',
		entity: ENTITY.DATA_VIZ,
		asyncQuery: dataVizAsyncQuery,
	},
	{
		relationKey: 'MARKET_PULSE',
		entity: ENTITY.DATA_VIZ,
		asyncQuery: dataVizAsyncQuery,
	},
	{
		relationKey: 'CONFIRMED_COMMUNITY_SURVEY',
		entity: ENTITY.DATA_VIZ,
		asyncQuery: dataVizAsyncQuery,
	},
];

const keyedSlotFieldName = (relationKey: string) =>
	KEYED_SLOT_PREFIX + relationKey;

const isKeyedSlotFieldName = (name: string) =>
	name.indexOf(KEYED_SLOT_PREFIX) === 0;

const keyedSlotByFieldName: { [fieldName: string]: KeyedSlotDef } = {};
KEYED_SLOTS.forEach((slot) => {
	keyedSlotByFieldName[keyedSlotFieldName(slot.relationKey)] = slot;
});

// il campo GraphQL (di sola lettura) che espone lo slot sul tipo Trend360
const KEYED_SLOT_READ_FIELDS: { [relationKey: string]: string } = {
	PARENT_MACROTREND: 'parentMacrotrend',
	ANTITHESIS_TREND: 'antithesisTrend',
	MACROTREND_SCORE_EVOLUTION: 'scoreEvolutionChart',
	MARKET_PULSE: 'marketPulseSurvey',
	CONFIRMED_COMMUNITY_SURVEY: 'communitySurvey',
};

/**
 * @param forceOptional true per NON rendere obbligatorio nemmeno il macrotrend padre:
 *        in modifica un record che ne e' privo (creato prima della feature o con lo
 *        slot svuotato) deve restare salvabile
 */
const toKeyedSlotField = (
	fieldName: string,
	forceOptional: boolean = false,
): IField => {
	const slot = keyedSlotByFieldName[fieldName];
	const field = toAsyncChoices(
		slot.entity,
		slot.asyncQuery,
		true,
		generateBaseField(
			fieldName,
			t(['Trend360', slot.relationKey, 'label'].join('/')),
			t(['Trend360', slot.relationKey, 'helpText'].join('/')),
		),
	);
	return !forceOptional && slot.relationKey === 'PARENT_MACROTREND'
		? toRequired(field)
		: field;
};

// whitelist dei campi "reali" del backend, derivata dalla disposizione
const FIELDS_BY_TYPE: { [key: string]: ReadonlyArray<string> } = {};
TREND360_TYPES.forEach((type) => {
	FIELDS_BY_TYPE[type] = PLACEMENT_BY_TYPE[type]
		.map((p) => p.name)
		.filter((name) => !isRelationFieldName(name) && !isKeyedSlotFieldName(name));
});

export const trend360FieldsForType = (
	type: Trend360TypeValue,
): ReadonlyArray<string> => FIELDS_BY_TYPE[type];

// unione (senza duplicati) dei campi "reali" di tutte le tipologie: base comune delle
// whitelist di modifica e dettaglio, che non conoscono la tipologia prima del caricamento
const ALL_TYPE_FIELDS: ReadonlyArray<string> = (() => {
	const names: string[] = [];
	TREND360_TYPES.forEach((type) => {
		FIELDS_BY_TYPE[type].forEach((name) => {
			if (names.indexOf(name) === -1) {
				names.push(name);
			}
		});
	});
	return names;
})();

/**
 * Whitelist del form di modifica: la tipologia del record non e' nota prima del
 * caricamento, quindi si generano i campi di TUTTE le tipologie e il transformer di
 * modifica tiene solo quelli che servono. `trendType` e' escluso perche' readonly
 * (non fa parte della Trend360PatchInput).
 */
export const TREND360_EDIT_FIELDS: ReadonlyArray<string> = ALL_TYPE_FIELDS;

const orderAndSection = (
	placements: ReadonlyArray<FieldPlacement>,
	fields: ReadonlyArray<IField>,
): ReadonlyArray<IField> => {
	const byName: { [key: string]: IField } = {};
	fields.forEach((f) => {
		byName[f.name] = f;
	});
	const ordered: IField[] = [];
	placements.forEach(({ name, section }) => {
		const field: any = byName[name];
		if (!field) {
			return;
		}
		if (section) {
			field.pre = <h2>{t(section)}</h2>;
		}
		ordered.push(field);
		delete byName[name];
	});
	// eventuali campi non elencati in coda
	fields.forEach((f) => {
		if (byName[f.name]) {
			ordered.push(f);
		}
	});
	return ordered;
};

// ------------------------------------------------------------------ transformer

// etichette per-tipo dei campi condivisi intro/content (stessa colonna, semantica diversa)
const SHARED_LABEL_FIELDS = ['intro', 'content'];

export const trend360CreateTransformer = (
	type: Trend360TypeValue,
): ITransformerFunction => (data) => {
	let fields: ReadonlyArray<IField> = data.fields.map((f) => {
		if (f.name === 'firstDetected') {
			return toRequired(f);
		}
		if (f.name === 'tags') {
			// la colonna e' una String con dentro un array JSON: nel form e' un campo a chip
			return generateStringListField(
				'tags',
				t`Trend360/tags/label`,
				t`Trend360/tags/helpText`,
			);
		}
		if (SHARED_LABEL_FIELDS.indexOf(f.name) > -1) {
			return {
				...(f as any),
				label: t(['Trend360', f.name, type, 'label'].join('/')),
			} as IField;
		}
		if (REPEATABLES[f.name]) {
			return toRepeatableField(f.name);
		}
		return f;
	});

	fields = fields
		.concat(RELATIONS_BY_TYPE[type].map(toRelationField))
		.concat(
			// gli slot keyed non sono campi della Trend360CreateInput: li aggiungo io
			PLACEMENT_BY_TYPE[type]
				.map((p) => p.name)
				.filter(isKeyedSlotFieldName)
				.map((fieldName) => toKeyedSlotField(fieldName)),
		);

	return { ...data, fields: orderAndSection(PLACEMENT_BY_TYPE[type], fields) };
};

// ------------------------------------------------------------------ dettaglio (sola lettura)

// tipo di entita' collegata per ogni campo lista Relation360_* (per entity2String) e
// alias della radice della DetailQuery che ne carica i dati (vedi Trend360DetailQuery:
// una radice per lista per evitare il prodotto cartesiano delle JOIN "sorelle")
const RELATION_LIST_TARGETS: { [field: string]: ENTITY } = {
	Relation360_Company360s: ENTITY.COMPANY_360,
	Relation360_Good360s: ENTITY.GOOD_360,
	Relation360_EditorialSections: ENTITY.EDITORIAL_SECTION,
	// NB - niente Relation360_DataVizs: i DataViz si collegano SOLO dai tre slot keyed
	// (nessuna tipologia espone la relazione generica, ne' nel form ne' nel dialogo)
	Relation360_Trend360s: ENTITY.TREND_360,
	Relation360_Magazines: ENTITY.MAGAZINE,
	Relation360_NewsletterIssues: ENTITY.NEWSLETTER_ISSUE,
	Relation360_Advs: ENTITY.ADV,
	Relation360_Awards: ENTITY.AWARD,
};

const RELATION_LIST_FIELDS = Object.keys(RELATION_LIST_TARGETS);

const relationListQueryAlias = (fieldName: string) =>
	fieldName.replace('Relation360_', 'rel');

// whitelist del dettaglio: derivata dall'unione dei campi di tutte le tipologie;
// il transformer filtra poi i campi scalari sulla tipologia del record, mentre le
// liste di relazione restano SEMPRE visibili: cosi' eventuali collegamenti legacy
// (creati prima delle restrizioni per tipologia del dialogo) restano almeno leggibili
export const TREND360_DETAIL_FIELDS: ReadonlyArray<string> = (() => {
	const names: string[] = ['status', 'trendType'].concat(
		ALL_TYPE_FIELDS as string[],
	);
	// i campi di sola lettura che espongono gli slot keyed non sono in FIELDS_BY_TYPE
	KEYED_SLOTS.forEach((slot) => {
		const readField = KEYED_SLOT_READ_FIELDS[slot.relationKey];
		if (names.indexOf(readField) === -1) {
			names.push(readField);
		}
	});
	return names.concat(RELATION_LIST_FIELDS);
})();

const formatJsonList = (name: string, raw: any): string => {
	if (!raw) {
		return '';
	}
	let rows: any;
	try {
		rows = JSON.parse(raw);
	} catch (e) {
		return String(raw);
	}
	if (!Array.isArray(rows)) {
		return String(raw);
	}
	if (name === 'signalSources') {
		return rows
			.map((r: any) => {
				if (!r || typeof r !== 'object') {
					return `• ${String(r)}`;
				}
				return `• ${t(['Trend360', 'signalSource', r.source].join('/'))}: ${
					r.contributionPercent !== null &&
					r.contributionPercent !== undefined
						? r.contributionPercent + '%'
						: '-'
				}`;
			})
			.join('\n');
	}
	if (name === 'earlySignals') {
		return rows
			.map((r: any) => {
				if (!r || typeof r !== 'object') {
					return `• ${String(r)}`;
				}
				return `• ${r.label}: ${
					r.value !== null && r.value !== undefined ? r.value : '-'
				}${
					r.unit
						? ' ' + t(['Trend360', 'earlySignalUnit', r.unit].join('/'))
						: ''
				}${r.description ? ' — ' + r.description : ''}`;
			})
			.join('\n');
	}
	return rows.map((r: any) => `• ${String(r)}`).join('\n');
};

/**
 * Transformer del dettaglio: mostra i campi scalari della sola tipologia del record,
 * rende leggibili le colonne JSON dei gruppi ripetibili e valorizza le liste
 * Relation360_* dalle radici dedicate della DetailQuery.
 */
export const trend360DetailTransformer: ITransformerFunction = (
	data,
	entity,
	queryResult,
) => {
	const type: Trend360TypeValue =
		(entity && entity.trendType) || 'MACROTREND';
	const visible = ['localId', 'status', 'trendType']
		.concat(FIELDS_BY_TYPE[type] as string[])
		// gli slot keyed previsti da questa tipologia, con il nome del campo di lettura
		.concat(
			PLACEMENT_BY_TYPE[type]
				.map((p) => p.name)
				.filter(isKeyedSlotFieldName)
				.map(
					(fieldName) =>
						KEYED_SLOT_READ_FIELDS[
							keyedSlotByFieldName[fieldName].relationKey
						],
				),
		)
		.concat(RELATION_LIST_FIELDS);

	const fields = data.fields
		.filter((f) => visible.indexOf(f.name) > -1)
		.map((f) => {
			if (JSON_LIST_FIELDS.indexOf(f.name) > -1) {
				return {
					...(f as any),
					type: 'Text',
					formatText: true,
					value: formatJsonList(f.name, f.value),
				} as IField;
			}
			// le liste Relation360_* vengono rese come testo: il tipo auto-generato
			// (EntityList) presuppone un campo "name" che Company360/Good360 non hanno.
			// I dati arrivano dalle radici aliasate della DetailQuery.
			if (RELATION_LIST_TARGETS[f.name]) {
				const target = RELATION_LIST_TARGETS[f.name];
				const root =
					queryResult &&
					(queryResult as any)[relationListQueryAlias(f.name)];
				const entities: any[] = (root && root[f.name]) || [];
				return {
					...(f as any),
					type: 'Text',
					formatText: true,
					value: entities
						// difensivo: se il valore arrivasse gia' "wrappato" da
						// setFieldsValues ({label, value}), si usa l'entita' interna
						.map((e) => (e && e.value ? e.value : e))
						.map((e) => `• ${entity2String(target, e)}`)
						.join('\n'),
				} as IField;
			}
			if (SHARED_LABEL_FIELDS.indexOf(f.name) > -1) {
				return {
					...(f as any),
					label: t(['Trend360', f.name, type, 'label'].join('/')),
				} as IField;
			}
			return f;
		});

	return { ...data, fields };
};

// ------------------------------------------------------------------ salvataggio

/**
 * Serializza il valore di un campo ripetibile nella sua colonna JSON (undefined se il
 * campo non e' un ripetibile). Unico punto per creazione E modifica: la forma salvata
 * non puo' divergere fra i due flussi.
 */
const serializeListFieldValue = (key: string, value: any): string | undefined => {
	if (TEXT_LIST_FIELDS.indexOf(key) > -1) {
		return JSON.stringify((value || []).map((row: any) => row.text));
	}
	if (OBJECT_LIST_FIELDS.indexOf(key) > -1) {
		return JSON.stringify(
			(value || []).map((row: any) => {
				const { type: _rowType, ...rest } = row;
				return rest;
			}),
		);
	}
	return undefined;
};

export interface Trend360SaveHandlers {
	beforeSaveTransformer: (values: any) => any;
	afterCreate: (result: any) => Promise<any>;
}

/**
 * Gestori di salvataggio per la creazione di un Trend360 di una data tipologia.
 *
 * beforeSaveTransformer: inietta `trendType`, serializza i gruppi ripetibili nelle
 * colonne JSON e mette da parte i valori dei campi `rel_*` (che non esistono nella
 * Trend360CreateInput) per afterCreate.
 *
 * afterCreate: preleva la selezione messa da parte (e la AZZERA, cosi' una mutation
 * fallita non lascia una selezione stantia da attribuire al submit successivo) e
 * collega il nuovo Trend360 alle entita' selezionate scrivendo le Relationships360
 * in entrambe le direzioni (convenzione del modulo 360).
 *
 * I due handler DEVONO essere usati in coppia (stessa istanza restituita da questa
 * factory): e' il motivo per cui vengono creati insieme.
 */
export const trend360SaveHandlers = (
	type: Trend360TypeValue,
): Trend360SaveHandlers => {
	let pendingRelationIds: ReadonlyArray<string> = [];
	let pendingKeyedSlots: ReadonlyArray<{
		relationKey: string;
		targetId: string;
	}> = [];

	return {
		beforeSaveTransformer: (values: any) => {
			const out: any = { trendType: type };
			const targetIds: string[] = [];
			const keyedSlots: { relationKey: string; targetId: string }[] = [];
			Object.keys(values).forEach((key) => {
				const value = values[key];
				if (isKeyedSlotFieldName(key)) {
					// slot singolo: diventa UNA Relationships360 con relation_key
					// (gia' trasformato in RefInput {id} dal campo AsyncChoices)
					if (value && value.id) {
						keyedSlots.push({
							relationKey: keyedSlotByFieldName[key].relationKey,
							targetId: value.id,
						});
					}
					return;
				}
				if (isRelationFieldName(key)) {
					// gia' trasformati in RefInput ({id}) dal beforeSaveTransformer
					// del campo AsyncChoices
					(value || []).forEach((ref: any) => {
						if (ref && ref.id) {
							targetIds.push(ref.id);
						}
					});
					return;
				}
				const serialized = serializeListFieldValue(key, value);
				if (serialized !== undefined) {
					out[key] = serialized;
					return;
				}
				out[key] = value;
			});
			pendingRelationIds = targetIds;
			pendingKeyedSlots = keyedSlots;
			return out;
		},

		afterCreate: async (result: any) => {
			const targetIds = pendingRelationIds;
			const keyedSlots = pendingKeyedSlots;
			pendingRelationIds = [];
			pendingKeyedSlots = [];
			if (!result || !result.id || (!targetIds.length && !keyedSlots.length)) {
				return;
			}
			// relazioni generiche: bidirezionali (convenzione del modulo 360)
			const creates = targetIds.reduce(
				(acc: any[], targetId: string) =>
					acc.concat(bidirectionalCreatePairs(result.id, targetId)),
				[],
			);
			// slot keyed: UNA sola direzione (il verso opposto si ricava da questa)
			keyedSlots.forEach((slot) => {
				creates.push(
					keyedCreatePair(result.id, slot.targetId, slot.relationKey),
				);
			});
			await applyRelationships360Bulk([], creates);
		},
	};
};

// ------------------------------------------------------------------ modifica

/**
 * Modifica di un Trend360.
 *
 * Il form di modifica e' quello di creazione con i valori caricati. Il motore
 * (setFieldsValues) sa valorizzare da solo i campi che sono davvero colonne - testi,
 * date, enum, media - ma NON sa nulla di:
 *  - `tags` e dei gruppi ripetibili, che in database sono stringhe JSON e nel form sono
 *    widget dedicati (il transformer di creazione li sostituisce con campi vuoti);
 *  - degli slot keyed e delle relazioni multiple, che non sono colonne e quindi non
 *    fanno nemmeno parte della Trend360PatchInput.
 *
 * Questa factory restituisce i tre pezzi che devono lavorare in coppia (transformer +
 * salvataggio) perche' condividono lo stato iniziale caricato: senza confrontare il
 * "prima" con il "dopo" non si puo' sapere quali relazioni togliere.
 */
export interface Trend360EditHandlers {
	transformer: ITransformerFunction;
	beforeSaveTransformer: (values: any) => any;
	afterSave: (result: any) => Promise<any>;
}

const parseJsonArray = (raw: any): any[] => {
	if (!raw) {
		return [];
	}
	try {
		const parsed = JSON.parse(raw);
		return Array.isArray(parsed) ? parsed : [];
	} catch (e) {
		// dato malformato: meglio un campo vuoto che un form che esplode
		return [];
	}
};

/**
 * Ricostruisce UNA riga di un gruppo ripetibile a partire dal dato salvato.
 * E' l'inverso del beforeSaveTransformer di generateSubFormField: la riga e' un SubForm
 * il cui valore e' { selected, fields }, con i valori assegnati PER NOME (non per
 * posizione) cosi' che aggiungere un campo ad un sotto-form non sposti i valori.
 */
const rowToSubFormField = (subFormType: SubForms, row: any): IField => {
	const name = generateRandomID();
	const fields = (subFormsConstructors[subFormType] || []).map(
		(rowField: IField) => {
			const field: any = { ...rowField };
			// le righe a testo singolo sono salvate come stringhe nude
			const rawValue =
				row && typeof row === 'object' ? row[field.name] : row;
			if (rawValue === undefined || rawValue === null) {
				return field;
			}
			if (field.type === 'Choices') {
				const choice = (field.choices || []).filter(
					(c: IChoice) => c.value === rawValue,
				)[0];
				field.value = choice || { value: rawValue, label: String(rawValue) };
			} else {
				field.value = rawValue;
			}
			return field;
		},
	);
	return {
		...(generateSubFormField(name, name, [subFormType], true) as any),
		value: { selected: subFormType, fields },
	} as IField;
};

const hydrateRepeatable = (fieldName: string, raw: any): IField[] =>
	parseJsonArray(raw).map((row) =>
		rowToSubFormField(REPEATABLES[fieldName].form, row),
	);

// entita' collegata -> campo lista Relation360_* che la contiene
const RELATION_LIST_BY_ENTITY: { [entity: string]: string } = {};
Object.keys(RELATION_LIST_TARGETS).forEach((listField) => {
	RELATION_LIST_BY_ENTITY[RELATION_LIST_TARGETS[listField]] = listField;
});

/** valore di un campo AsyncChoices singolo, nella forma prodotta da setFieldsValues */
const toAsyncChoiceValue = (entity: ENTITY, record: any) =>
	record ? { label: entity2String(entity, record), value: record } : undefined;

export const trend360EditHandlers = (): Trend360EditHandlers => {
	// stato iniziale, riempito dal transformer e letto dal salvataggio
	let trendType: Trend360TypeValue = 'MACROTREND';
	let initialKeyedTargets: { [relationKey: string]: string } = {};
	let initialRelationEntities: { [fieldName: string]: any[] } = {};
	let pendingKeyedTargets: { [relationKey: string]: string | null } = {};
	let pendingRelationIds: { [fieldName: string]: string[] } = {};

	return {
		transformer: (data, entity, queryResult) => {
			trendType = (entity && entity.trendType) || 'MACROTREND';
			initialKeyedTargets = {};
			initialRelationEntities = {};

			// IMPORTANT - la whitelist passata al motore e' l'unione dei campi di TUTTE
			// le tipologie (la tipologia non e' nota prima del caricamento): qui vanno
			// tenuti solo quelli della tipologia del record, altrimenti orderAndSection
			// li accoderebbe in fondo al form e si potrebbero salvare valori privi di
			// senso per quel tipo (e invisibili nel dettaglio, che invece filtra).
			const typeFields = FIELDS_BY_TYPE[trendType];
			let fields: ReadonlyArray<IField> = data.fields
				.filter((f) => typeFields.indexOf(f.name) > -1)
				.map((f) => {
				if (f.name === 'firstDetected') {
					// obbligatorio solo se il record un valore ce l'ha gia': i record
					// creati prima di questa feature hanno first_detected NULL e
					// altrimenti non sarebbero piu' modificabili
					return entity && entity.firstDetected ? toRequired(f) : f;
				}
				if (f.name === 'tags') {
					return {
						...(generateStringListField(
							'tags',
							t`Trend360/tags/label`,
							t`Trend360/tags/helpText`,
						) as any),
						value: parseJsonArray(entity && entity.tags),
					} as IField;
				}
				if (SHARED_LABEL_FIELDS.indexOf(f.name) > -1) {
					return {
						...(f as any),
						label: t(['Trend360', f.name, trendType, 'label'].join('/')),
					} as IField;
				}
				if (REPEATABLES[f.name]) {
					return {
						...(toRepeatableField(f.name) as any),
						value: hydrateRepeatable(f.name, entity && entity[f.name]),
					} as IField;
				}
				return f;
			});

			// slot keyed: il valore arriva dal campo di sola lettura corrispondente
			const keyedFields = PLACEMENT_BY_TYPE[trendType]
				.map((p) => p.name)
				.filter(isKeyedSlotFieldName)
				.map((fieldName) => {
					const slot = keyedSlotByFieldName[fieldName];
					const record =
						entity && entity[KEYED_SLOT_READ_FIELDS[slot.relationKey]];
					if (record && record.id) {
						initialKeyedTargets[slot.relationKey] = record.id;
					}
					// come per firstDetected: se lo slot e' vuoto il campo NON diventa
					// obbligatorio, altrimenti un record senza macrotrend padre non
					// sarebbe piu' salvabile
					return {
						...(toKeyedSlotField(fieldName, !record) as any),
						value: toAsyncChoiceValue(slot.entity, record),
					} as IField;
				});

			// relazioni multiple: i valori arrivano dalle radici aliasate della query
			const relationFields = RELATIONS_BY_TYPE[trendType].map((rel) => {
				const listField = RELATION_LIST_BY_ENTITY[rel.entity];
				const root =
					queryResult &&
					(queryResult as any)[relationListQueryAlias(listField)];
				const records: any[] = (root && root[listField]) || [];
				initialRelationEntities[rel.name] = records;
				return {
					...(toRelationField(rel) as any),
					value: records.map((record) => ({
						label: entity2String(rel.entity, record),
						value: record,
					})),
				} as IField;
			});

			fields = fields.concat(relationFields).concat(keyedFields);

			return {
				...data,
				fields: orderAndSection(PLACEMENT_BY_TYPE[trendType], fields),
			};
		},

		beforeSaveTransformer: (values: any) => {
			const out: any = {};
			// IMPORTANT - a differenza della creazione NON si inietta `trendType`: la
			// tipologia e' readonly lato server e non fa parte della Trend360PatchInput
			pendingKeyedTargets = {};
			pendingRelationIds = {};
			Object.keys(values).forEach((key) => {
				const value = values[key];
				if (isKeyedSlotFieldName(key)) {
					// null esplicito = slot svuotato dall'utente
					pendingKeyedTargets[keyedSlotByFieldName[key].relationKey] =
						value && value.id ? value.id : null;
					return;
				}
				if (isRelationFieldName(key)) {
					pendingRelationIds[key] = (value || [])
						.filter((ref: any) => ref && ref.id)
						.map((ref: any) => ref.id);
					return;
				}
				const serialized = serializeListFieldValue(key, value);
				if (serialized !== undefined) {
					out[key] = serialized;
					return;
				}
				out[key] = value;
			});
			return out;
		},

		afterSave: async (result: any) => {
			if (!result || !result.id) {
				return;
			}

			// prima si calcola il diff (senza rete): nel caso di gran lunga piu' comune -
			// una modifica ai soli campi scalari - non c'e' NULLA da fare qui e non si
			// paga nessuna query aggiuntiva.
			const creates: any[] = [];
			const keyedDeleteKeys: string[] = [];
			const relationRemovals: { entity: ENTITY; localId: number }[] = [];

			Object.keys(pendingKeyedTargets).forEach((relationKey) => {
				// null (svuotato) e undefined (mai esistito) sono lo stesso "vuoto"
				const targetId = pendingKeyedTargets[relationKey] || null;
				const initialId = initialKeyedTargets[relationKey] || null;
				if (targetId === initialId) {
					return; // invariato
				}
				if (targetId) {
					// il backend sostituisce da solo la riga precedente dello slot
					creates.push(keyedCreatePair(result.id, targetId, relationKey));
				} else {
					keyedDeleteKeys.push(relationKey);
				}
			});

			Object.keys(pendingRelationIds).forEach((fieldName) => {
				const submittedIds = pendingRelationIds[fieldName];
				const initialRecords = initialRelationEntities[fieldName] || [];
				const initialIds = initialRecords.map((r: any) => r.id);
				submittedIds
					.filter((id) => initialIds.indexOf(id) === -1)
					.forEach((id) => {
						creates.push(...bidirectionalCreatePairs(result.id, id));
					});
				initialRecords
					.filter((record: any) => submittedIds.indexOf(record.id) === -1)
					.forEach((record: any) => {
						const rel = RELATIONS_BY_TYPE[trendType].filter(
							(r) => r.name === fieldName,
						)[0];
						relationRemovals.push({
							entity: rel.entity,
							localId: record.localId,
						});
					});
			});

			const hasRemovals =
				keyedDeleteKeys.length > 0 || relationRemovals.length > 0;
			if (!creates.length && !hasRemovals) {
				return;
			}

			const deleteIds: string[] = [];
			if (hasRemovals) {
				// per cancellare serve l'id della riga di giunzione, non quello
				// dell'entita' (le sole aggiunte non ne hanno bisogno).
				// IMPORTANT - se questa query fallisce l'errore DEVE propagare: ogni
				// delete del diff nasce da qui, quindi proseguire con una lista vuota
				// salverebbe le aggiunte scartando silenziosamente tutte le rimozioni.
				// Il chiamante (FunctionalEntityEdit) lo mostra nella modale errori.
				const linkRes = await client.query<any, any>({
					query: RELATIONS_LINKS_QUERY,
					variables: { id: result.id },
					fetchPolicy: 'no-cache',
				});
				const links: RelationLinks = parseLinks(linkRes.data);
				keyedDeleteKeys.forEach((relationKey) => {
					deleteIds.push(...findKeyedJoinIds(links, relationKey));
				});
				relationRemovals.forEach((removal) => {
					deleteIds.push(
						...findJoinIds(links, removal.entity, removal.localId),
					);
				});
			}

			if (!creates.length && !deleteIds.length) {
				return;
			}
			await applyRelationships360Bulk(deleteIds, creates);
		},
	};
};
